import React, { useMemo, useState } from 'react'
import styles from "../../../../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss";
import globalStyle from "../../../../../../assets/styles/global.module.scss";
import { Divider, Grid, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GlobalPopUp from '../../../../../../components/utility/globalPopUp';
import CatalogOrderProductRow from './catalogOrderProductRow';
import { thousandSeparator } from '../../../../../../utils/constants';

const ORDER_STATUS_CHIP_CLASS: Record<string, string> = {
    paid: styles.catalogOrderStatusChipPaid,
    not_paid: styles.catalogOrderStatusChipNotPaid,
    failed: styles.catalogOrderStatusChipNotPaid,
    pending: styles.catalogOrderStatusChipPending,
    canceled: styles.catalogOrderStatusChipCanceled,
};

const ORDER_STATUS_DOT_CLASS: Record<string, string> = {
    paid: styles.catalogOrderStatusDotPaid,
    not_paid: styles.catalogOrderStatusDotNotPaid,
    failed: styles.catalogOrderStatusDotNotPaid,
    pending: styles.catalogOrderStatusDotPending,
    canceled: styles.catalogOrderStatusDotCanceled,
};

const ORDER_STATUS_LABEL: Record<string, string> = {
    paid: 'Paid',
    not_paid: 'Not Paid',
    pending: 'Pending',
    canceled: 'Canceled',
    failed: 'Failed'
};

const normalizeOrderStatus = (status?: string) => status?.toLowerCase()?.replace(/\s+/g, '_') ?? '';

const getOrderStatusChipClass = (status?: string) => {
    const normalizedStatus = normalizeOrderStatus(status);
    return `${styles.catalogOrderStatusChip} ${ORDER_STATUS_CHIP_CLASS[normalizedStatus] ?? ''}`;
};

const getOrderStatusDotClass = (status?: string) => {
    const normalizedStatus = normalizeOrderStatus(status);
    return `${styles.catalogOrderStatusDot} ${ORDER_STATUS_DOT_CLASS[normalizedStatus] ?? ''}`;
};

const getOrderStatusLabel = (status?: string) => {
    const normalizedStatus = normalizeOrderStatus(status);
    return ORDER_STATUS_LABEL[normalizedStatus] ?? status ?? '';
};

const OrderDetailTemplatePreview = (props: any) => {
    
    const { productItems, currency, totalAmount, referenceId, bodyText, orderStatus } = props;
    const [openPopup, setOpenPopup] = useState(false);

    const getTotalItemCount = useMemo(() => {
        const totalItemCount = productItems?.reduce((total: number, item: any) => {
            return total + item?.quantity || 0;
        }, 0);
        return totalItemCount;
    }, productItems);

    const getAmount = (value: number, offset: number) => {
        return (value ?? 0) / (offset ?? 1);
    }

    const getTotalAmount = useMemo(() => {
        return getAmount(totalAmount?.value, totalAmount?.offset) 
    }, [totalAmount]);

    return (
        <>
            <Grid item className={`${styles.viewOrderMainGrid} ${globalStyle.relativePosition}`}>
                <Grid item className={styles.viewOrderUpperContainer}>
                    <Grid container className={`${globalStyle.displayFlexStyle} ${globalStyle.flexColumn}`} flexWrap={"nowrap"} gap={1}>
                        <Grid item className={styles.viewOrderGrid} onClick={() => setOpenPopup(true)}>
                            <Grid container className={`${globalStyle.flexColumn} ${globalStyle.gapOne}`}>
                                <Grid item>
                                    <Typography className={`${globalStyle.tertiaryFontSize} ${globalStyle.secondaryFontWeight}`}>
                                        {`ORDER #${referenceId ?? ''}`}
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Divider className={styles.viewOrderMsgDivider} />
                                </Grid>
                                <Grid item>
                                    <Grid container direction={'column'}>
                                        <Grid item>
                                            <Typography className={`${globalStyle.tertiaryFontSize} ${globalStyle.secondaryFontWeight}`}>
                                                {productItems?.map((item: any) => item?.name).join(", ")}
                                            </Typography>
                                        </Grid>
                                        <Typography className={`${styles.viewOrderMsgItemCount}`}>
                                            {`${getTotalItemCount ?? 0} item${getTotalItemCount > 1 ? 's' : ''}`}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Divider className={styles.viewOrderMsgDivider} />
                                </Grid>
                                <Grid item>
                                    <Grid container className={`${globalStyle.displayFlex} ${globalStyle.justifyContentSpaceBetween}`}>
                                        <Grid item>
                                            <Typography className={`${globalStyle.tertiaryFontSize} ${globalStyle.secondaryFontWeight}`}>
                                                Total
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography className={`${globalStyle.tertiaryFontSize} ${globalStyle.secondaryFontWeight}`}>
                                                {`${currency ?? ""} ${getTotalAmount}`}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item>
                            <Typography className={`${globalStyle.tertiaryFontSize} ${globalStyle.secondaryFontWeight}`}>
                                {bodyText ?? ''}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid container className={`${styles.viewSentCardBtnGrid} ${globalStyle.justifyCenter}`} onClick={() => setOpenPopup(true)}>
                    <Grid item>
                        <Typography className={styles.viewSentCartBtn}>
                            View details
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container className={`${styles.viewSentCardBtnGrid} ${globalStyle.justifyCenter}`}>
                    <Grid item>
                        <Typography className={styles.viewSentCartBtn}>
                            Pay now
                        </Typography>
                    </Grid>
                </Grid>

                {openPopup && 
                    <GlobalPopUp
                        open={openPopup}
                        handleClose={() => setOpenPopup(false)}
                        onClose={() => setOpenPopup(false)}
                        borderRadius="8px"
                        paperMinWidth="330px"
                        paperMaxwidth="380px"
                        overflow="hidden"
                        className={`${styles.catalogOrderPopupDialog} ${styles.catalogOrderPopupDialogCustomHeader}`}
                    >
                            <div className={styles.catalogOrderPopupHeader}>
                                <div className={styles.catalogOrderPopupHeaderTop}>
                                    <span className={styles.catalogOrderPopupHeaderTitle}>Order Details</span>
                                    <IconButton
                                        aria-label="close"
                                        onClick={() => setOpenPopup(false)}
                                        className={styles.catalogOrderPopupCloseBtn}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </div>
                                <span className={styles.catalogOrderPopupHeaderOrderId}>{`ORDER #${referenceId ?? ''}`}</span>
                                {orderStatus && (
                                    <span className={getOrderStatusChipClass(orderStatus)}>
                                        <span className={getOrderStatusDotClass(orderStatus)} />
                                        {getOrderStatusLabel(orderStatus)}
                                    </span>
                                )}
                            </div>
                            <div className={styles.catalogOrderPopupHeaderDivider} />
                            <Grid item className={styles.catalogOrderPopupContent}>
                                {productItems?.map((item: any, index: number) => {
                                    const productName = item?.name ?? ''
                                    const productImage = item?.ecom_order_item_product_image ?? item?.image?.link ?? ''
                                    const quantity = item?.quantity ?? 0
                                    const unitPrice = getAmount(item?.amount?.value, item?.amount?.offset)
                                    const lineTotal = unitPrice * quantity

                                    return (
                                        <React.Fragment key={item?.product_retailer_id ?? index}>
                                            {index > 0 && <div className={styles.catalogOrderPopupProductDivider} />}
                                            <CatalogOrderProductRow
                                                productName={productName}
                                                productImage={productImage}
                                                attributes={null}
                                                quantity={quantity}
                                                currency={currency}
                                                unitPrice={unitPrice}
                                                lineTotal={lineTotal}
                                                showQtyPriceBreakdown 
                                            />
                                        </React.Fragment>
                                    )
                                })}
                            </Grid>

                            <Grid container className={styles.catalogOrderPopupFooter} justifyContent="space-between" alignItems="center">
                                <Grid item>
                                    <Typography className={styles.catalogOrderPopupFooterLabel}>{`${productItems?.length ?? 0} items total`}</Typography>
                                </Grid>
                                <Grid item>
                                    <Typography className={styles.catalogOrderPopupFooterTotal}>{`${currency ?? ''} ${thousandSeparator(getTotalAmount)}`}</Typography>
                                </Grid>
                            </Grid>
                    </GlobalPopUp>
                }
            </Grid>
        </>
    )
}

export default OrderDetailTemplatePreview
